import {Perspective, ScreenRegion} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/layout/Perspective';
import {
    HeavyPanelContent,
    IPanelDatum,
    Panel,
    PanelGroup,
    PanelHeader,
    PanelId,
    PanelKind
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/PanelState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import {action} from 'mobx';

export class GeneralPanelsController
{
    public isHeavyPanelKind(group:PanelGroup, panelKind:PanelKind):boolean
    {
        const heavyPanelContents = group.heavyPanelContents;
        if( !heavyPanelContents )
            return false;

        return heavyPanelContents.findIndex((hpc:HeavyPanelContent) => (hpc.forPanelKind === panelKind)) !== -1;
    }

    public findRegionOfPanel(panel:Panel):ScreenRegion | null
    {
        const perspective = controllers.ui.perspective.findPanelPerspective(panel);
        const screenRegion = perspective.findPanelRegion(panel);

        return screenRegion;
    }

    public isPanelSelectedAndActive(group:PanelGroup, panel:Panel):boolean
    {
        const isPanelSelected = group.selectedPanel ? group.selectedPanel.id === panel.id : false;
        const isInActivePanelSet = controllers.ui.perspective.isPanelGroupInActivePerspective(group);

        return isPanelSelected && isInActivePanelSet;
    }

    @bind
    public isHeavyContentVisible(group:PanelGroup, hpc:HeavyPanelContent):boolean
    {
        const selectedPanel = this.getSelectedPanel(group);
        if( !selectedPanel )
            return false;

        return selectedPanel.kind === hpc.forPanelKind;
    }

    @bind
    public getSelectedPanel(group:PanelGroup):Panel | null
    {
        return group.selectedPanel;
    }

    @action.bound
    public removePanelById(panelId:PanelId):void
    {
        const panel = this._findPanelById(panelId);

        if( !panel )
        {
            console.log(`Error: removePanelById(): cannot find panel with panelId: ${panelId}`);
            return;
        }

        this.removePanel(panel);
    }

    private _findPanelById(panelId:PanelId):Panel | null
    {
        const allPerspectives = controllers.ui.perspective.getAllPerspectives();

        let foundPanel = null;
        allPerspectives.map((p:Perspective) =>
        {
            const panel = p.findPanelById(panelId);
            if( panel )
                foundPanel = panel;
        });

        return foundPanel;
    }

    @action.bound
    public removePanel(panel:Panel):void
    {
        let idx = panel.panelGroup.panels.findIndex((p:Panel) => (p.id === panel.id));

        if( idx !== -1 )
        {
            const panelGroup = panel.panelGroup;
            const panels = panelGroup.panels;

            panels.splice(idx, 1);

            const numPanelsRemaining = panels.length;
            if( numPanelsRemaining > 0 )
            {
                const lastPanelIndex = numPanelsRemaining - 1;

                if( idx > lastPanelIndex )
                    idx = lastPanelIndex;

                panelGroup.selectedPanel = panels[idx];
            }
            else
                panelGroup.selectedPanel = null;
        }
    }

    @action.bound
    public selectFirstPanelOfKind(kind:PanelKind):void
    {
        const panel = this.findFirstPanelOfKind(kind);
        if( panel )
            this.selectPanel(panel.id);
    }

    public findFirstPanelOfKind(kind:PanelKind):Panel | null
    {
        const pController = controllers.ui.perspective;
        const allPerspectives = pController.getAllPerspectives();

        for( let perspective of allPerspectives )
        {
            let panel = perspective.findFirstPanelByKind(kind);
            if( panel )
                return panel;
        }

        return null;
    }

    @action.bound
    public selectPanel(panelId:PanelId):void
    {
        const pController = controllers.ui.perspective;
        const panel = pController.findPanelById(panelId);

        if( !panel )
        {
            console.warn(`Warning: cannot find panel by id: ${panelId}`);
            return;
        }

        panel.panelGroup.selectedPanel = panel;
    }

    @bind
    public getOrCreatePanel(perspective:Perspective,
                            region:ScreenRegion,
                            kind:PanelKind,
                            header:PanelHeader,
                            datum:IPanelDatum,
                            element:any):Panel
    {

        let existingPanel = perspective.findPanelByDatum(datum);

        if( existingPanel )
            return existingPanel;
        else
            return this.insertPanel(perspective,
                region,
                kind,
                header,
                datum,
                element);
    }

    @bind
    public removeAnyUnpinnedOfKind(perspective:Perspective,
                                   kind:PanelKind):void
    {
        const allUnpinnedOfKind = perspective.getAllUnpinnedOfKind(kind);
        allUnpinnedOfKind.map((p:Panel) => (this.removePanel(p)));
    }

    @bind
    public removePanelByDatum(datum:IPanelDatum):void
    {
        const allPerspectives = controllers.ui.perspective.getAllPerspectives();

        allPerspectives.map((p:Perspective) =>
        {
            this._removePanelInPerspective(p, datum);
        });
    }

    private _removePanelInPerspective(perspective:Perspective, datum:IPanelDatum)
    {
        const panel = perspective.findPanelByDatum(datum);

        if( panel )
            this.removePanel(panel);
    }

    @bind
    public insertPanel(perspective:Perspective,
                       region:ScreenRegion,
                       kind:PanelKind,
                       header:PanelHeader,
                       datum:IPanelDatum,
                       element:any):Panel
    {
        const newPanelId = IdUtil.newGUID();
        const panel = this._insertPanel(newPanelId, perspective, region, kind, header, datum, element);

        return panel;
    }

    @bind
    private _insertPanel(panelId:PanelId,
                         perspective:Perspective,
                         region:ScreenRegion,
                         kind:PanelKind,
                         header:PanelHeader,
                         datum:IPanelDatum,
                         element:any):Panel
    {
        const panelGroup = perspective.getPanelGroupForRegion(region);

        const newPanel = Panel.ofHeader(panelId, kind, panelGroup, header, Perspective.defaultWrap(element));
        newPanel.datum = datum;

        perspective.addPanelDirect(region, kind, newPanel);
        return newPanel;
    }
}
